import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "primereact/button";
import { I, T } from "services/lang/LanguageService";
import CalendarWrapper from "components/basic/calendar-wrapper/CalendarWrapper";
import { SortDirection } from "gql/generated/types";
import useTimeState from "hooks/dashboard/useTimeState";
import { Instant } from "@js-joda/core";
import { useClearDatasetData, useGetDataInfoRange } from "api/api-hooks/datasets-hooks";
import useChartInDialogStore from "store/chart-in-dialog-store";
import { registerAllModules } from "handsontable/registry";
import { CellChange, RangeType } from "handsontable/common";
import { DatasetDataApi } from "api/DatasetDataApi";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { ColumnSettings } from "handsontable/settings";
import { DweenTimeUnitEnum, fmtDatetimeAdvanced, toInstant } from "utils/time-utils";
import { HotTable, HotTableClass } from "@handsontable/react";
import { useTimezone } from "hooks/useTimezone";
import { calculateSteps, defaultFromDateTime, defaultToDateTime } from "../charts/utils";
import { DatasetDataInputTableProps, DatasetObject, DatasetValueMap, UpdateDatasetData } from "./DatasetDataInputTableTypes";
import "handsontable/styles/handsontable.css";
import "handsontable/styles/ht-theme-main.css";
import "handsontable/styles/ht-theme-horizon.css";
import "./DatasetDataInputTable.scss";

registerAllModules();

export default function DatasetDataInputTable(props: DatasetDataInputTableProps) {
  const tz = useTimezone();

  const { mutate } = useClearDatasetData();

  const [allSteps, setAllSteps] = useState<Array<Instant>>([]);
  const [data, setData] = useState<Array<Array<number | string | null>>>([]);
  const [updatedDatasets, setUpdatedDatasets] = useState<Array<UpdateDatasetData>>([]);

  const { setDialogVisibility, setDateFrom, setDateTo, setHeader, setIdentifier, setIdentifiers } =
    useChartInDialogStore();

  const hotTableRef = useRef<HotTableClass | null>(null);

  const defaultFromDate = useMemo(
    () => props.from || defaultFromDateTime({ zoneId: tz.company, loadConf: props.loadConf }),
    [props.from, props.loadConf]
  );

  const defaultToDate = useMemo(
    () => props.to || defaultToDateTime({ zoneId: tz.company, loadConf: props.loadConf }),
    [props.to, props.loadConf]
  );

  const timeState = useTimeState({
    from: props.from || defaultFromDate.toString(),
    to: props.to || defaultToDate.toString(),
  });

  const { data: datasets } = useGetDataInfoRange(
    props.datasets.map(d => d.identifier),
    {
      timeStart: timeState.from?.toISOString() || Instant.now().toString(),
      timeEnd: timeState.to?.toISOString(),
    }
  );

  useEffect(() => {
    handleStepsCount();
  }, [timeState.from, timeState.to, datasets]);

  const openChartDialog = (identifier: string, identifiers?: string[], from?: Instant, to?: Instant, header?: string) => {
    setIdentifier(identifier);
    setIdentifiers(identifiers || [identifier]);
    setDateFrom(from || Instant.now());
    setDateTo(to || Instant.now());
    setHeader(header || T("word.chart"));
    setDialogVisibility(true);
  };

  const handleStepsCount = () => {
    if (timeState.from && timeState.to) {
      const stepsArr = calculateSteps(
        toInstant(timeState.from),
        toInstant(timeState.to),
        props?.stepSize || 1,
        props?.stepUnit || DweenTimeUnitEnum.HOURS,
        tz.company
      );

      setAllSteps(props.sort === SortDirection.Asc ? stepsArr : stepsArr.reverse());

      if (datasets && stepsArr.length) {
        const initialData = stepsArr.map(step => {
          const row: Array<number | string | null> = [toInstant(step).toString()];
          datasets.forEach(ds => {
            const dataPoint = ds.data.find(point => toInstant(point.key).toEpochMilli() === toInstant(step).toEpochMilli());
            row.push(dataPoint?.value?.toFixed(ds.decimalPlaces ?? 2) ?? null);
          });
          return row;
        });
        setData(initialData);
      }
    }
  };

  const columns: ColumnSettings[] | { identifier: string } = datasets
    ? [
      {
        data: 0,
        title: T("word.date"),
        readOnly: true,
        width: 125,
        renderer(instance, td, row, col, prop, value) {
          td.innerText = fmtDatetimeAdvanced(value, tz.display, { pattern: props.datePattern });

          return td;
        },
      },
      ...datasets.map((ds, index) => {
        const isEditingDisabled = props.datasets.find(d => d.identifier === ds.identifier)?.disableEditing;
        const overriddenTitle = props.datasets.find(d => d.identifier === ds.identifier)?.overriddenTitle;
        let title = `${I(overriddenTitle || ds.title || ds.identifier)}${ds.unit ? `, ${ds.unit}` : ""}`;
        const maxSymbolsInNameRow = props.maxSymbolsInNameRow ?? 30;

        if (title.length > maxSymbolsInNameRow) {
          title = title.split("").reduce((acc, char, i) => {
            if (i > 0 && i % maxSymbolsInNameRow === 0) acc += "<br>";
            return acc + char;
          }, "");
        }

        return {
          data: index + 1,
          title,
          type: "numeric",
          allowInvalid: true,
          readOnly: isEditingDisabled,
          identifier: ds.identifier,
          validator: (value: any, callback: (isValid: boolean) => void) => {
            if (value === null || typeof value === "string") {
              if (value && value.length > 0) {
                easasAlert({
                  summary: T("alert.error"),
                  detail: T("alert.invalid-input-numeric", { value }),
                  severity: "error",
                });
              }
              callback(false);
            } else {
              callback(true);
            }
          },
        };
      }),
    ]
    : [];

  const handleAfterChange = (changes: CellChange[] | null) => {
    if (!changes) return;
    if (Array.isArray(changes)) {
      setData(prevData => {
        const newData = [...prevData];
        changes.forEach(([rowIndex, colIndex, oldValue, newValue]) => {
          if (oldValue !== newValue) {
            const parsedValue =
              newValue !== null && newValue !== "" && !isNaN(Number(newValue))
                ? Number(newValue)
                : newValue === ""
                  ? null
                  : newValue;

            newData[rowIndex][colIndex as number] = parsedValue;

            const timestamp = newData[rowIndex]?.[0] ?? null;
            const column = columns[colIndex as number];
            if (!column) {
              easasAlert({
                summary: T("alert.error"),
                detail: "Failed to update data: Invalid column index",
                severity: "error",
              });
              return
            }
            const { identifier } = column as DatasetObject;

            setUpdatedDatasets(prev => {
              const existing = prev.find(r => r.identifier === identifier);
              const updatedValues = { ...(existing?.values || {}) };
              if (timestamp !== null) {
                updatedValues[timestamp] = parsedValue;
              }
              return [
                ...prev.filter(r => r.identifier !== identifier),
                { identifier, values: updatedValues },
              ];
            });
          }
        });
        return newData;
      });
    }
  };

  const clearData = (from: string, to: string, identifiers: string[]) => {
    const timeStart = from;
    const timeEnd = to;
    const query = { timeStart, timeEnd, clearAll: false, identifiers };

    mutate(query, {
      onSuccess: () => {
        easasAlert({
          summary: T("alert.dataset-cleared"),
          detail: T("alert.data-has-been-cleared"),
          severity: "success",
        });
      },
      onError: error => {
        easasAlert({ summary: T("alert.clearing-failed"), detail: `${error}`, severity: "error" });
      },
    });
  };

  const updateData = () => {
    if (!updatedDatasets.length) {
      easasAlert({
        summary: T("alert.warn"),
        detail: T("alert.no-changes-made"),
        severity: "warn",
      });
      return;
    }

    const datasetsToClear: { from: string; to: string; identifiers: string[] }[] = [];
    const validDatasets: UpdateDatasetData[] = [];

    updatedDatasets.forEach(dataset => {
      const validValues: DatasetValueMap = {};
      const identifiersToClear: string[] = [];

      Object.entries(dataset.values).forEach(([timestamp, value]) => {
        if (typeof value === "string" || value === null) {
          identifiersToClear.push(dataset.identifier);
          datasetsToClear.push({
            from: timestamp,
            to: timestamp,
            identifiers: [dataset.identifier],
          });
        } else {
          validValues[timestamp] = value as number;
        }
      });

      if (Object.keys(validValues).length > 0) {
        validDatasets.push({
          identifier: dataset.identifier,
          values: validValues,
        });
      }
    });

    datasetsToClear.forEach(({ from, to, identifiers }) => {
      clearData(from, to, identifiers);
    });

    if (validDatasets.length > 0) {
      const formattedDatasets = validDatasets.map(dataset => ({
        identifier: dataset.identifier,
        values: Object.fromEntries(
          Object.entries(dataset.values)
            .filter(([value]) => value !== null)
            .map(([timestamp, value]) => [timestamp, Number(value)])
        ),
      }));
      DatasetDataApi.updateData(formattedDatasets)
        .then(() => {
          easasAlert({
            summary: T("alert.success"),
            detail: T("alert.datasets-updated"),
            severity: "success",
          });
          setUpdatedDatasets([]);
        })
        .catch(() => {
          easasAlert({
            summary: T("alert.error"),
            detail: T("alert.error-while-updating-datasets"),
            severity: "error",
          });
        });
    } else {
      easasAlert({
        summary: T("alert.warn"),
        detail: T("alert.no-valid-data-to-update"),
        severity: "warn",
      });
    }
  };

  const handleBeforePaste = (dataToPaste: any[][], coords: RangeType[]) => {
    const hot = hotTableRef.current?.hotInstance;
    if (!hot) return;

    const startVisualRow = coords[0]?.startRow ?? 0;
    const startCol = coords[0]?.startCol ?? 0;
    const allChanges: CellChange[] = [];

    setData(prev => {
      const updated = [...prev];

      for (let i = 0; i < dataToPaste.length; i++) {
        const visualRow = startVisualRow + i;
        const physicalRow = hot.toPhysicalRow(visualRow);
        if (physicalRow == null) continue;

        const rowData = dataToPaste[i];
        const newRow = [...updated[physicalRow]];

        for (let j = 0; j < rowData.length; j++) {
          const colIndex = startCol + j;
          if (colIndex >= 0 && colIndex < newRow.length) {
            const oldValue = newRow[colIndex];
            const raw = rowData[j];
            const newValue =
              raw === "" ? null :
                !isNaN(Number(raw)) ? Number(raw) : raw;

            if (oldValue !== newValue) {
              newRow[colIndex] = newValue;
              allChanges.push([physicalRow, colIndex, oldValue, newValue]);
            }
          }
        }

        updated[physicalRow] = newRow;
      }

      return updated;
    });

    hot.render();

    if (allChanges.length > 0) {
      handleAfterChange(allChanges);
    }

    return false;
  };

  return (
    <div className="dataset-data-input-table">
      <div className="flex flex-row gap-3 mb-3">
        <div className="flex flex-row gap-3 align-self-center">
          <CalendarWrapper id="from" value={timeState.from} onChange={timeState.setFrom} debounceTimeInMs={300} />
          <CalendarWrapper id="to" value={timeState.to} onChange={timeState.setTo} debounceTimeInMs={300} />
        </div>
        <Button
          label={T("action.update-datasets")}
          icon="pi pi-refresh"
          className="component-editor-button icon-blue"
          onClick={updateData}
          disabled={!updatedDatasets.length}
        />
      </div>
      <HotTable
        themeName="ht-theme-horizon"
        ref={hotTableRef}
        data={data}
        columnSorting
        manualColumnResize
        manualRowResize
        licenseKey="non-commercial-and-evaluation"
        beforePaste={handleBeforePaste}
        afterChange={handleAfterChange}
        copyPaste={{ pasteMode: 'overwrite' }}
        height={Math.min(allSteps.length * 25 + 40, props.tableHeight || 400)}
        columns={columns}
        dropdownMenu={{
          items: {
            openChart: {
              name: T("action.open-chart"),
              callback: (key, selection) => {
                const selectedColumn = selection[0]?.start?.col;
                const identifier =
                  "identifier" in columns[selectedColumn]
                    ? columns[selectedColumn].identifier
                    : undefined;
                if (identifier) openChartDialog(identifier);
              },
            },
          },
        }}
      />
    </div>
  );
}
